'use client';

import { Typography } from '@mui/material';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  aiModelOptions,
  lengthOptions,
  toneOptions
} from '@/app/[locale]/Ai-Text-Generator/Text-Generation-Functions/dropdownOptions';
import { handleGenerate } from '@/app/[locale]/Ai-Text-Generator/Text-Generation-Functions/HandelGenerate';
import {
  DropDownBox,
  StyledForm,
  StyledResults,
  StyledScrollableBox,
  StyledWrapper
} from '@/app/[locale]/Ai-Text-Generator/Text-Generation-Functions/PageStyling';
import { premiumFeatures } from '@/app/[locale]/Ai-Text-Generator/Text-Generation-Functions/premiumFeatures';
import CTABOX from '@/features/components/CTA/CtaBox';
import SmallPremiumAlert from '@/features/components/CTA/PremiumADSmall';
import Dropdown from '@/features/components/DropDown';
import InputFieldWithTemplates from '@/features/components/Inputfields/InputFieldWithTemplates';
import { getFlagCode, languageOptions } from '@/features/components/LANGUAGES_FLAGS';
import StyledMainBox from '@/features/components/MainBox';
import ResponseCardFlashScore from '@/features/components/resultDisplay/ResponseCardFlashScore';
import StreamingResponseCard from '@/features/components/resultDisplay/StreamingResponseCard';
import CustomButton from '@/features/components/SubmitButton';
import { handleCopy, handleDelete, handlePerformance } from '@/features/Functions/ResultFunctions';
import useTextGenerator from '@/hooks/useTextGenerator';

const TextGenerationPage = () => {
  const intl = useIntl();
  const {
    placeholderText,
    inputText,
    setInputText,
    tone,
    setTone,
    length,
    setLength,
    language,
    setLanguage,
    model,
    setModel,
    fingerPrint,
    setFingerPrint,
    loading,
    setLoading,
    premium,
    setPremium,
    responses,
    setResponses,
    streamingResponse,
    setStreamingResponse,
    isStreaming,
    setIsStreaming,
    counter,
    setCounter,
    isTouched,
    setIsTouched,
    showAd,
    setShowAd,
    inputRef
  } = useTextGenerator();
  const { formatMessage } = useIntl();

  const handleTemplateSelect = (value: string) => {
    setInputText(value);
  };

  const handleBlur = () => {
    setIsTouched(true);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputText.trim()) {
      setIsTouched(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return;
    }
    setShowAd(false);
    await handleGenerate(
      e,
      inputText,
      tone,
      length,
      language,
      model,
      counter,
      fingerPrint,
      setLoading,
      setIsStreaming,
      setStreamingResponse,
      setResponses,
      setCounter
    );
  };

  return (
    <StyledMainBox>
      <StyledWrapper>
        <StyledForm onSubmit={onSubmit}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            <FormattedMessage id="home.text-generator.title" />
          </Typography>

          <InputFieldWithTemplates
            placeholderText={placeholderText}
            inputText={inputText}
            setInputText={setInputText}
            handleBlur={handleBlur}
            isTouched={isTouched}
            inputRef={inputRef as React.RefObject<HTMLTextAreaElement | HTMLInputElement>}
            handleTemplateSelect={handleTemplateSelect}
            premium={premium}
          />
          <DropDownBox>
            <Dropdown
              label={intl.formatMessage({ id: 'home.text-generator.language.dropdown' })}
              value={language}
              onChange={e => setLanguage(e.target.value)}
              options={languageOptions(intl, getFlagCode)}
            />
            <Dropdown
              label={intl.formatMessage({ id: 'home.text-generator.tone.dropdown' })}
              value={tone}
              onChange={e => setTone(e.target.value)}
              options={toneOptions(intl, premium)}
              premium={premium}
            />
          </DropDownBox>
          <DropDownBox>
            <Dropdown
              label={intl.formatMessage({ id: 'home.text-generator.length.dropdown' })}
              value={length}
              onChange={e => setLength(e.target.value)}
              options={lengthOptions(intl)}
            />
            <Dropdown
              label={intl.formatMessage({ id: 'home.text-generator.AImodel.dropdown' })}
              value={model}
              onChange={e => setModel(e.target.value)}
              options={aiModelOptions(intl, premium)}
              premium={premium}
            />
          </DropDownBox>
          <CustomButton
            disabled={isStreaming || loading}
            counter={counter}
            maxCount={3}
            loading={false}
            premium={premium}
            label={formatMessage({ id: 'home.text-generator.generate.button' })}
          />
        </StyledForm>

        <StyledResults>
          <StyledScrollableBox>
            {isStreaming && (
              <StreamingResponseCard
                streamingResponse={streamingResponse}
                placeholderText={formatMessage({ id: 'Result.text-generator.placeholder' })}
              />
            )}
            {responses.length > 0
              ? responses.map((response, index) => (
                  <ResponseCardFlashScore
                    key={index}
                    response={response}
                    index={index}
                    responses={responses}
                    setResponses={setResponses}
                    handleCopy={handleCopy}
                    handleDelete={handleDelete}
                    handlePerformance={handlePerformance}
                    premium={premium}
                  />
                ))
              : responses.length === 0 &&
                !isStreaming && (
                  <StreamingResponseCard
                    streamingResponse={streamingResponse}
                    placeholderText={formatMessage({ id: 'Result.text-generator.placeholder' })}
                  />
                )}
          </StyledScrollableBox>
        </StyledResults>
      </StyledWrapper>
      <CTABOX>
        <SmallPremiumAlert
          title={<FormattedMessage id="home.premium.title" />}
          features={premiumFeatures}
        />
      </CTABOX>
    </StyledMainBox>
  );
};

export default TextGenerationPage;
